import React, { useState } from 'react';
import { BarChart3, Shield, Briefcase, Brain, Sparkles, Users, Award, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-]+/g, "")
        .replace(/\-\-+/g, "-")
        .replace(/^-+/, "")
        .replace(/-+$/, "");
};
const ExploreTopCourses = () => {
    const { courses, isFetching } = useSelector(state => state?.course || []);
    const courseList = courses?.all?.courses || [];
    
    const [showAll, setShowAll] = useState(false);

    const filteredCourses = courseList.filter(p => p.courseViews >= 10);
    const displayedCourses = showAll ? filteredCourses : filteredCourses.slice(0, 5);

    const features = [
        { icon: Sparkles, text: "Most in-demand skills on offer" },
        { icon: Users, text: "Global partnerships with the best" },
        { icon: Award, text: "World-class Bootcamps" },
        { icon: Zap, text: "8X better learning experience" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="!absolute inset-0">
                <div className="!absolute top-20 left-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="!absolute top-40 right-20 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
                <div className="!absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
            </div>

            {/* Geometric Pattern Overlay */}
            <div className="!absolute inset-0 opacity-10">
                <div className="!absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}>
                </div>
            </div>

            <div className="relative z-10 container mx-auto px-6 py-16">
                <div className="grid lg:grid-cols-2 gap-16 items-center min-h-screen">

                    {/* Left Content */}
                    <div className="space-y-8 text-white">
                        <div className="space-y-6">
                            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium">
                                <Sparkles className="w-4 h-4 mr-2 text-yellow-300" />
                                <span>Trusted by 5 Million+ Learners</span>
                            </div>

                            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                                <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                                    5 Million+
                                </span>
                                <br />
                                <span className="text-white">Learners Enrolled.</span>
                                <br />
                                <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                                    Will You Be Next?
                                </span>
                            </h1>

                            <p className="text-xl !text-blue-100 leading-relaxed max-w-lg">
                                Enroll in our world-class, highly engaging, and outcome-driven professional upskilling programs designed for success
                            </p>
                        </div>

                        {/* Features Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {features.map((feature, index) => (
                                <div key={index} className="flex items-center space-x-3 group">
                                    <div className="flex-shrink-0 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110">
                                        <feature.icon className="w-6 h-6 text-cyan-300" />
                                    </div>
                                    <span className="text-blue-100 font-medium group-hover:text-white transition-colors">
                                        {feature.text}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* CTA Button */}
                        <div className="pt-6">
                            <Link to="/courses">
                                <button className="group relative px-8 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold rounded-2xl shadow-2xl hover:shadow-cyan-500/25 transform hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                                    <span className="relative z-10">Start Your Journey Today</span>
                                    <div className="!absolute inset-0 bg-gradient-to-r from-cyan-300 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </button>
                            </Link>
                        </div>
                    </div>

                    {/* Right Content - Programs */}
                    <div className="space-y-6">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-white mb-2">Explore our top programs</h2>
                            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-400 mx-auto rounded-full"></div>
                        </div>

                        <div className="space-y-6">
                            {displayedCourses.map((program, index) => (
                                <Link to={`/${slugify(program.CourseName)}`} state={{ course: program }}>
                                    <div key={index} className="group relative mt-3 bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-500 border border-white/30" style={{ animationDelay: `${index * 100}ms` }}>
                                        {/* Hover Gradient Overlay */}
                                        <div className="!absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl z-0"></div>

                                        <div className="relative z-10 flex items-center space-x-4">
                                            {/* Icon Container */}
                                            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                                <Brain className="w-7 h-7 text-white" />
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-800 group-hover:text-gray-900 transition-colors">
                                                    {program.CourseName}
                                                </h3>

                                                {/* Progress Indicator */}
                                                <div className="mt-2 flex items-center space-x-2">
                                                    <div className="flex-1 h-2 bg-gray-300 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-green-400 to-blue-500 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-1000 origin-left"
                                                        ></div>
                                                    </div>
                                                    <span className="text-xs text-gray-600 group-hover:text-gray-800">
                                                        {Math.floor(Math.random() * 30) + 60}% enrolled
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Navigation Arrow */}
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            ))}

                            {/* Show More / Show Less Button */}
                            {filteredCourses.length > 5 && (
                                <div className="text-center mt-4">
                                    <button
                                        onClick={() => setShowAll(!showAll)}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:shadow-md transition-all duration-300"
                                    >
                                        {showAll ? <ChevronUp /> : <ChevronDown />}
                                    </button>
                                </div>
                            )}
                        </div>


                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 gap-4 mt-8">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20 hover:bg-white/20 transition-all duration-300">
                                <div className="text-2xl font-bold text-white">98%</div>
                                <div className="text-blue-200 text-sm">Success Rate</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20 hover:bg-white/20 transition-all duration-300">
                                <div className="text-2xl font-bold text-white">24/7</div>
                                <div className="text-blue-200 text-sm">Support</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Gradient */}
            <div className="!absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-blue-900 to-transparent"></div>
        </div>
    );
};

export default ExploreTopCourses;